import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';

const STORAGE_KEY = 'mars_onboarding_pending';
const DELAY_NORMAL = 3500;
const DELAY_UPLOAD = 8000;
const DELAY_EXTRA = 4000;

const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
const USER_TYPE_OPTIONS = ['Student', 'Employed', 'Job Seeking', 'Freelancer'];
const INDUSTRY_OPTIONS = ['Technology', 'Design', 'Marketing', 'Sales', 'Finance', 'Healthcare', 'Education', 'Operations', 'Legal', 'Other'];

const STEP_TITLES = ['Basic Info', 'Contact Details', 'Professional Profile'];
const STEP_SUBTITLES = [
  'Tell us a little about yourself',
  'How can employers reach you?',
  'What do you do professionally?',
];

const inp = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1.5px solid #E8EDF2',
  borderRadius: 8,
  padding: '10px 13px',
  fontSize: 13,
  fontFamily: 'Inter, system-ui, sans-serif',
  background: '#FAFBFC',
  color: '#1F2937',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const lbl = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: '#6B7280',
  marginBottom: 5,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const focusStyle = (e) => {
  e.target.style.borderColor = '#EF1D28';
  e.target.style.boxShadow = '0 0 0 3px rgba(239,29,40,0.08)';
  e.target.style.background = 'white';
};
const blurStyle = (e) => {
  e.target.style.borderColor = '#E8EDF2';
  e.target.style.boxShadow = 'none';
  e.target.style.background = '#FAFBFC';
};

const Field = ({ label, children }) => (
  <div>
    <label style={lbl}>{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = 'text' }) => (
  <input
    type={type}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    style={inp}
    onFocus={focusStyle}
    onBlur={blurStyle}
  />
);

const SelectInput = ({ value, onChange, options, placeholder }) => (
  <select value={value} onChange={e => onChange(e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}>
    <option value="">{placeholder || 'Select…'}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const StepIndicator = ({ step }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 24 }}>
    {[1, 2, 3].map((s, i) => (
      <React.Fragment key={s}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: s <= step ? 'linear-gradient(135deg,#EF1D28,#C5161D)' : '#E8EDF2',
          color: s <= step ? 'white' : '#9CA3AF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, flexShrink: 0,
          boxShadow: s === step ? '0 2px 8px rgba(239,29,40,0.35)' : 'none',
          transition: 'all 0.2s',
        }}>{s}</div>
        {i < 2 && (
          <div style={{
            width: 40, height: 2,
            background: s < step ? 'linear-gradient(90deg,#EF1D28,#C5161D)' : '#E8EDF2',
            transition: 'background 0.3s',
          }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const Step1 = ({ data, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
    <Field label="First Name">
      <TextInput value={data.firstName} onChange={v => onChange('firstName', v)} placeholder="Jane" />
    </Field>
    <Field label="Last Name">
      <TextInput value={data.lastName} onChange={v => onChange('lastName', v)} placeholder="Smith" />
    </Field>
    <Field label="Gender">
      <SelectInput value={data.gender} onChange={v => onChange('gender', v)} options={GENDER_OPTIONS} placeholder="Select gender…" />
    </Field>
    <Field label="I am a…">
      <SelectInput value={data.userType} onChange={v => onChange('userType', v)} options={USER_TYPE_OPTIONS} placeholder="Select type…" />
    </Field>
  </div>
);

const Step2 = ({ data, onChange }) => (
  <div style={{ display: 'grid', gap: 14 }}>
    <Field label="Phone Number">
      <TextInput value={data.phone} onChange={v => onChange('phone', v)} placeholder="+1 555 000 0000" type="tel" />
    </Field>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      <Field label="City">
        <TextInput value={data.city} onChange={v => onChange('city', v)} placeholder="New York" />
      </Field>
      <Field label="Country">
        <TextInput value={data.country} onChange={v => onChange('country', v)} placeholder="United States" />
      </Field>
    </div>
  </div>
);

const Step3 = ({ data, onChange }) => (
  <div style={{ display: 'grid', gap: 14 }}>
    <Field label="Job Title">
      <TextInput value={data.jobTitle} onChange={v => onChange('jobTitle', v)} placeholder="e.g. Software Engineer" />
    </Field>
    <Field label="Industry">
      <SelectInput value={data.industry} onChange={v => onChange('industry', v)} options={INDUSTRY_OPTIONS} placeholder="Select industry…" />
    </Field>
    <Field label="Short Bio / Summary">
      <textarea
        value={data.bio}
        onChange={e => onChange('bio', e.target.value)}
        placeholder="A brief description of your background and what you're looking for…"
        rows={4}
        style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }}
        onFocus={focusStyle}
        onBlur={blurStyle}
      />
    </Field>
  </div>
);

export const OnboardingModal = () => {
  const { user, updateProfile } = useAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [pendingShow, setPendingShow] = useState(false);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', gender: '', userType: '',
    phone: '', city: '', country: '',
    jobTitle: '', industry: '', bio: '',
  });
  const timerRef = useRef(null);

  const isBuilderRoute = location.pathname.startsWith('/builder');

  // Seed form from user profile when user loads
  useEffect(() => {
    if (!user) return;
    const p = user.userProfile || {};
    setFormData(prev => ({
      ...prev,
      firstName: p.firstName || prev.firstName,
      lastName: p.lastName || prev.lastName,
      gender: p.gender || prev.gender,
      userType: p.userType || prev.userType,
      phone: p.phone || prev.phone,
      city: p.city || prev.city,
      country: p.country || prev.country,
      jobTitle: p.jobTitle || prev.jobTitle,
      industry: (p.jobPreferences && p.jobPreferences.industry) || prev.industry,
      bio: p.profile || prev.bio,
    }));
  }, [user]);

  // Trigger logic
  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(STORAGE_KEY) !== 'true') return;
    if (user.onboardingCompleted === true) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // Cross-device fallback: new account within 24h, flag not yet set locally
    const createdAt = user.createdAt ? new Date(user.createdAt) : null;
    const isRecentSignup = createdAt && (Date.now() - createdAt.getTime() < 24 * 60 * 60 * 1000);
    if (localStorage.getItem(STORAGE_KEY) !== 'true' && isRecentSignup && !user.onboardingCompleted) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }

    const hasPendingFile = !!localStorage.getItem('mars_pending_file');
    const delay = hasPendingFile ? DELAY_UPLOAD : DELAY_NORMAL;

    timerRef.current = setTimeout(() => {
      // Re-check if import is still running
      if (localStorage.getItem('mars_pending_file')) {
        timerRef.current = setTimeout(() => {
          if (isBuilderRoute) {
            setPendingShow(true);
          } else {
            setVisible(true);
          }
        }, DELAY_EXTRA);
        return;
      }
      if (isBuilderRoute) {
        setPendingShow(true);
      } else {
        setVisible(true);
      }
    }, delay);

    return () => clearTimeout(timerRef.current);
  }, [user]); // intentionally omit isBuilderRoute — we only want this to fire when user first loads

  // Show when user leaves builder if pendingShow is set
  useEffect(() => {
    if (pendingShow && !isBuilderRoute) {
      setVisible(true);
      setPendingShow(false);
    }
  }, [isBuilderRoute, pendingShow]);

  const handleChange = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const buildProfilePayload = () => {
    const current = user?.userProfile || {};
    const base = {
      ...current,
      firstName: formData.firstName,
      lastName: formData.lastName,
      gender: formData.gender,
      userType: formData.userType,
      phone: formData.phone,
      city: formData.city,
      country: formData.country,
      jobTitle: formData.jobTitle,
      profile: formData.bio,
      jobPreferences: {
        ...(current.jobPreferences || {}),
        industry: formData.industry,
      },
    };
    return base;
  };

  const handleSaveAndContinue = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await updateProfile(buildProfilePayload());
      if (step < 3) {
        setStep(s => s + 1);
      } else {
        handleComplete();
      }
    } catch (_e) {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkipStep = () => {
    if (step < 3) {
      setStep(s => s + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    localStorage.removeItem(STORAGE_KEY);
    setVisible(false);
    api.completeOnboarding().catch(() => {});
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes ob-fadeUp {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.48)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        zIndex: 9500,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
        <div style={{
          width: '100%', maxWidth: 480,
          background: 'white',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
          animation: 'ob-fadeUp 0.3s cubic-bezier(0.16,1,0.3,1) both',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg,#EF1D28 0%,#C5161D 100%)',
            padding: '20px 24px 18px',
            position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.65)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 2 }}>
                  Step {step} of 3
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'white', letterSpacing: -0.3 }}>
                  {STEP_TITLES[step - 1]}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>
                  {STEP_SUBTITLES[step - 1]}
                </div>
              </div>
              <button
                onClick={handleComplete}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
                  color: 'white', fontSize: 11, fontWeight: 600, padding: '6px 12px',
                  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}
              >
                Skip All
              </button>
            </div>
          </div>

          {/* Step indicator */}
          <div style={{ padding: '20px 24px 0' }}>
            <StepIndicator step={step} />
          </div>

          {/* Form body */}
          <div style={{ padding: '4px 24px 20px' }}>
            {step === 1 && <Step1 data={formData} onChange={handleChange} />}
            {step === 2 && <Step2 data={formData} onChange={handleChange} />}
            {step === 3 && <Step3 data={formData} onChange={handleChange} />}
          </div>

          {/* Footer */}
          {saveError && (
            <div style={{
              margin: '0 24px 4px', padding: '8px 12px',
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 6, color: '#DC2626', fontSize: 12,
            }}>
              {saveError}
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 24px 20px',
            borderTop: '1px solid #F3F4F6',
          }}>
            <button
              onClick={handleSkipStep}
              style={{
                background: 'none', border: 'none', color: '#9CA3AF',
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                fontFamily: 'inherit', padding: '8px 4px',
              }}
            >
              Skip this step
            </button>

            <button
              onClick={handleSaveAndContinue}
              disabled={saving}
              style={{
                background: saving ? '#E5E7EB' : 'linear-gradient(135deg,#B00D15,#EF1D28)',
                color: saving ? '#9CA3AF' : 'white',
                border: 'none', borderRadius: 10,
                padding: '11px 24px', fontSize: 14, fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                boxShadow: saving ? 'none' : '0 4px 14px rgba(239,29,40,0.35)',
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'all 0.15s',
              }}
            >
              {saving && (
                <div style={{
                  width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white', borderRadius: '50%',
                  animation: 'ob-spin 0.7s linear infinite',
                }} />
              )}
              <style>{`@keyframes ob-spin{to{transform:rotate(360deg)}}`}</style>
              {step === 3 ? 'Finish' : 'Save & Continue'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
